import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import { seo } from '@shared/seo.config'
import { useI18n } from '@/i18n'
import { BrandMark } from '@/components/Brand'

/**
 * The frame shared by the sign-in and register pages.
 *
 * Two columns on a wide screen, one on a phone — and the pitch column is the
 * one that disappears, so a small screen shows the input fields immediately
 * instead of making someone scroll past marketing to reach them.
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  highlights,
}: {
  title: string
  subtitle: string
  children: ReactNode
  footer: ReactNode
  highlights: string[]
}) {
  const { t } = useI18n()

  return (
    <div data-upbo-src="src/components/AuthShell.tsx:31" className="grid min-h-[calc(100dvh-4rem)] lg:grid-cols-[1.1fr_1fr]">
      {/* Pitch column — hidden below lg so the form comes first on a phone. */}
      <div data-upbo-src="src/components/AuthShell.tsx:33" className="relative hidden overflow-hidden bg-brand-ink text-brand-ink-foreground lg:flex">
        <div data-upbo-src="src/components/AuthShell.tsx:34" aria-hidden className="tex-grid pointer-events-none absolute inset-0" />
        <div data-upbo-src="src/components/AuthShell.tsx:35" aria-hidden className="glow pointer-events-none absolute -top-32 right-0 size-[30rem]" />

        <div data-upbo-src="src/components/AuthShell.tsx:37" className="relative flex w-full flex-col justify-between p-12">
          <Link to="/" className="inline-flex w-fit items-center gap-2.5">
            <span data-upbo-src="src/components/AuthShell.tsx:39" className="grid size-8 place-items-center rounded-lg bg-brand-ink-foreground text-brand-ink">
              <BrandMark className="size-5" />
            </span>
            <span data-upbo-src="src/components/AuthShell.tsx:42" className="font-display text-sm font-semibold tracking-tight">{seo.siteName}</span>
          </Link>

          <div data-upbo-src="src/components/AuthShell.tsx:45">
            <h2 data-upbo-src="src/components/AuthShell.tsx:46" className="max-w-md font-display text-4xl font-semibold tracking-tight">
              {t('auth.panel.title')}
            </h2>
            <p data-upbo-src="src/components/AuthShell.tsx:49" className="mt-5 max-w-md leading-relaxed text-brand-ink-foreground/70">
              {t('auth.panel.body')}
            </p>
            <ul data-upbo-src="src/components/AuthShell.tsx:52" className="mt-10 flex flex-col gap-4">
              {highlights.map((item) => (
                <li data-upbo-src="src/components/AuthShell.tsx:54" key={item} className="flex items-start gap-3 text-sm leading-relaxed text-brand-ink-foreground/80">
                  <span data-upbo-src="src/components/AuthShell.tsx:55" className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-brand-ink-foreground/15 text-brand-ink-foreground">
                    <Check className="size-3" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p data-upbo-src="src/components/AuthShell.tsx:64" className="text-xs text-brand-ink-foreground/50">{seo.siteName}</p>
        </div>
      </div>

      {/* Form column — vertically centred, shown on every screen. */}
      <div data-upbo-src="src/components/AuthShell.tsx:69" className="flex flex-col justify-center px-4 py-12 sm:px-6">
        <div data-upbo-src="src/components/AuthShell.tsx:70" className="mx-auto w-full max-w-md">
          <h1 data-upbo-src="src/components/AuthShell.tsx:71" className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
          <p data-upbo-src="src/components/AuthShell.tsx:72" className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
          <div data-upbo-src="src/components/AuthShell.tsx:73" className="mt-8">{children}</div>
          <div data-upbo-src="src/components/AuthShell.tsx:74" className="mt-6 text-sm text-muted-foreground">{footer}</div>
        </div>
      </div>
    </div>
  )
}

/** Form-level error. Returns null when there is none, so it can be rendered unconditionally. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p data-upbo-src="src/components/AuthShell.tsx:85" role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      {message}
    </p>
  )
}